import { useApplicationActions } from "@/components/applications/shared/useApplicationActions";
import { useQuery } from "@apollo/client/react";
import ModalProvider from "@repo/components/src/globalProviders/modal";
import { Box } from "@repo/components/src/ui/box";
import {
  Button,
  ButtonIcon,
  ButtonSpinner,
  ButtonText,
} from "@repo/components/src/ui/button";
import { HStack } from "@repo/components/src/ui/hstack";
import {
  ModalBody,
  ModalCloseButton,
  ModalHeader,
} from "@repo/components/src/ui/modal";
import { Skeleton } from "@repo/components/src/ui/skeleton";
import { PostulationScoreDocument } from "@repo/graphql/documents/postulation/query.generated";
import { UserCampaignScoreDocument } from "@repo/graphql/documents/recommendation/query.generated";
import { GetUserProfileDocument } from "@repo/graphql/documents/user/query.generated";
import { PlatformCodeEnum } from "@repo/graphql/gql/graphql";
import { ArrowUpRight, Heart, Send, ThumbsDown, X } from "lucide-react-native";
import React, { useMemo, useState } from "react";
import ProfileDrawerActions from "./action";
import ProfileBody from "./profileBody";
import { TabType } from "./types";
import { UserCampaignScoreFragmentFragment } from "@repo/graphql/documents/recommendation/fragment.generated";

type Props = {
  userId: string;
  brandId: string;
  campaignId?: string;
  initialTab?: TabType;
  scoreData?: UserCampaignScoreFragmentFragment;
  postulationId?: string;
  showApplicationActions?: boolean;
  applicationActionsMode?: "applicant" | "preselected";
  onSendWorkOrder?: () => void;
  onUnshortlist?: () => void;
  /** After a preselect from the drawer, once it has closed (e.g. go to the
   *  preselection). */
  onPreselected?: () => void;
};

const ProfileDrawer = React.memo((props: Props) => {
  const { initialTab = "instagram" } = props;
  const tabs: {
    value: TabType;
    label: string;
    hide: boolean;
    disabled: boolean;
  }[] = [
    { value: "instagram", label: "Instagram", disabled: false, hide: false },
    { value: "tiktok", label: "TikTok", disabled: false, hide: false },
    { value: "niches", label: "Nichos", disabled: false, hide: false },
    { value: "reviews", label: "Reviews", disabled: false, hide: false },
    {
      value: "brand-activity",
      label: "Actividad con tu marca",
      disabled: false,
      hide: false,
    },
    {
      value: "application",
      label: "Postulación",
      disabled: props.campaignId === undefined,
      hide: props.campaignId === undefined,
    },
    {
      value: "work-order",
      label: "Orden de trabajo",
      disabled: props.campaignId === undefined,
      hide: props.campaignId === undefined,
    },
  ];
  const [activeTab, setActiveTab] = useState<TabType>(initialTab);
  const userProfileQuery = useQuery(GetUserProfileDocument, {
    variables: {
      id: props.userId,
      instagramPlatform: PlatformCodeEnum.Instagram,
      tiktokPlatform: PlatformCodeEnum.TikTok,
      brandId: props.brandId,
      campaignId: props.campaignId,
    },
    fetchPolicy: "cache-and-network",
  });
  const { data, loading } = userProfileQuery;

  const scoreVariables = {
    campaignId: props.campaignId ?? "",
    userId: props.userId,
  };
  const skipScore =
    props.scoreData !== undefined || props.campaignId === undefined;
  // An applicant's Fit IA comes with their application; the recommendations
  // query (gated by its own plan quota) only covers creators who never applied.
  const { data: postulationScoreData } = useQuery(PostulationScoreDocument, {
    variables: scoreVariables,
    skip: skipScore,
  });
  const postulation = postulationScoreData?.postulationResource.getForUser;
  const { data: scoreQueryData } = useQuery(UserCampaignScoreDocument, {
    variables: scoreVariables,
    skip:
      skipScore || postulationScoreData === undefined || postulation != null,
  });

  const resolvedScoreData = useMemo<
    UserCampaignScoreFragmentFragment | undefined
  >(() => {
    if (props.scoreData) return props.scoreData;
    if (postulation) return postulation.usercampaignscore ?? undefined;
    const edge =
      scoreQueryData?.recommendation.campaignRecommendations.edges[0];
    if (!edge) return undefined;
    return edge.node;
  }, [props.scoreData, postulation, scoreQueryData]);

  const mode = props.applicationActionsMode ?? "applicant";
  const showApplicantActions = Boolean(
    props.showApplicationActions && mode === "applicant" && props.postulationId,
  );
  const showPreselectedActions = Boolean(
    props.showApplicationActions && mode === "preselected",
  );
  const {
    reject,
    like,
    loadingReject,
    loadingLike,
    loading: actionsLoading,
  } = useApplicationActions(props.postulationId, {
    onRejectComplete: ModalProvider.hide,
    onLikeComplete: () => {
      ModalProvider.hide();
      props.onPreselected?.();
    },
  });
  return (
    <>
      <ModalHeader>
        <HStack space="xl">
          {tabs
            .filter((tab) => {
              if (tab.hide) return false;
              return true;
            })
            .map((tab) => (
              <Button
                key={tab.value}
                onPress={() => setActiveTab(tab.value)}
                variant="link"
                className="gap-x-1"
                action={activeTab === tab.value ? "brand" : "muted"}
                size="sm"
                isDisabled={tab.disabled}
              >
                <ButtonIcon as={ArrowUpRight} />
                <ButtonText className="font-normal">{tab.label}</ButtonText>
              </Button>
            ))}
        </HStack>
        <Box className="flex-row items-center gap-2">
          {showApplicantActions ? (
            <>
              <Button
                action="negative"
                variant="outline"
                size="sm"
                rounded="full"
                className="w-10 h-10 p-0 border-2 border-error-500"
                onPress={reject}
                isDisabled={actionsLoading}
              >
                {loadingReject ? (
                  <ButtonSpinner className="text-error-500" size="small" />
                ) : (
                  <ButtonIcon as={ThumbsDown} className="text-error-500" />
                )}
              </Button>
              <Button
                action="brand"
                variant="solid"
                size="sm"
                rounded="full"
                className="w-10 h-10 p-0"
                onPress={like}
                isDisabled={actionsLoading}
              >
                {loadingLike ? (
                  <ButtonSpinner size="small" className="text-typography-0" />
                ) : (
                  <ButtonIcon as={Heart} />
                )}
              </Button>
            </>
          ) : null}
          {showPreselectedActions ? (
            <>
              <Button
                action="negative"
                variant="outline"
                size="sm"
                rounded="full"
                className="w-10 h-10 p-0 border-2 border-error-500"
                onPress={props.onUnshortlist}
                isDisabled={!props.onUnshortlist}
              >
                <ButtonIcon as={X} className="text-error-500" />
              </Button>
              <Button
                action="brand"
                variant="solid"
                size="sm"
                rounded="full"
                className="w-10 h-10 p-0"
                onPress={props.onSendWorkOrder}
                isDisabled={!props.onSendWorkOrder}
              >
                <ButtonIcon as={Send} />
              </Button>
            </>
          ) : null}
          <ProfileDrawerActions
            userProfileQuery={userProfileQuery}
            brandId={props.brandId}
            campaignId={props.campaignId}
          />
          <ModalCloseButton>
            <Button size="xl" variant="link" onPress={ModalProvider.hide}>
              <ButtonIcon as={X} className="text-typography-900" />
            </Button>
          </ModalCloseButton>
        </Box>
      </ModalHeader>

      <ModalBody className="pb-6" showsVerticalScrollIndicator={false}>
        <Skeleton isLoaded={!loading} className="min-h-96">
          {data?.userResource.get && (
            <ProfileBody
              user={data?.userResource.get}
              activeTab={activeTab}
              brandId={props.brandId}
              campaignId={props.campaignId}
              scoreData={resolvedScoreData}
            />
          )}
        </Skeleton>
      </ModalBody>
    </>
  );
});

ProfileDrawer.displayName = "ProfileDrawer";

export default ProfileDrawer;
